/**
 * The ONE credential-holder state for the app shell.
 *
 * ## Why this file exists
 *
 * Before it, the shell answered "am I signed in?" with eighteen independent
 * predicates — a Stytch hook here, a `?.session_jwt` there, a `logged_in`
 * boolean off a daemon frame, a localStorage probe, an `accountId` truthiness
 * check — and only ONE of them (`holdsIdentityAtom`) looked at expiry. Every
 * observed defect was two of them disagreeing: `SessionActivationGate` mounted
 * the whole app tree off an expired JWT while every hook's `enabled` gate said
 * no, producing a fully rendered, permanently empty shell.
 *
 * There is now exactly one piece of auth state in the renderer — the
 * {@link HolderPhase} held by `holderPhaseBaseAtom` — and everything else in the
 * shell is a READ-ONLY PROJECTION of it.
 *
 * ## THE DERIVATION RULE (inherited from `@localagent/sdk`'s `authState.ts`)
 *
 * A holder changes phase ONLY by (a) a local material event it itself caused, or
 * (b) applying a verdict. It NEVER computes a phase from raw material. Two
 * corollaries this module must physically preserve:
 *
 *   - A locally-decoded `exp` may emit `ClockExpiry` (`usable -> degraded`,
 *     RECOVERABLE) and may NEVER reach `no_credential`. That is why
 *     {@link authClockAtom}'s writer dispatches `ClockExpiry` and nothing else.
 *   - Only a `rejected` verdict or an explicit `LocalClear` reaches
 *     `no_credential`.
 *
 * The reducer itself is NOT reimplemented here. `applyEvent` is imported from
 * the SDK, which is the sole owner of the transition table across every surface
 * (web renderer, desktop renderer, the Rust daemon). A second reducer would be
 * the same defect this file deletes, one layer up.
 *
 * ## Persistence
 *
 * The phase — not raw material — is what persists. Restoring a phase the holder
 * previously reached is not derivation; reconstructing one from a bag of tokens
 * would be. `getOnInit: true` makes the read SYNCHRONOUS, which is what let the
 * old `readPersistedStytchPair()` localStorage probe (predicate #6) be deleted
 * outright rather than reproduced.
 */

import { atom } from 'jotai';
import {
  classifyVerdict,
  decodeSessionJwt,
  degradedReasonOf,
  expiresAtOf,
  holdsIdentity,
  identityOf,
  INITIAL_PHASE,
  materialOf,
  type CredentialIdentity,
  type CredentialMaterial,
  type DegradedReason,
  type HolderEvent,
  type HolderPhase,
  type HolderPhaseName,
  type TransitionResult,
} from '@localagent/sdk';
import type { IdentityController } from '@localagent/sdk';
import { configureAtomDevtools } from '../utils/tracking';

/**
 * The persisted key and version live in the SDK (`HOLDER_PHASE_KEY` — the SDK
 * owns the storage seam, sdk ADR-011, and the restore-time `ClockExpiry`,
 * sdk ADR-020). This shell knows the ONE controller.
 */

/**
 * The controller binding. Set ONCE by `bindIdentity` at SDK install; until
 * then the shell holds `INITIAL_PHASE` and every writer refuses (there is no
 * controller to route through — "SDK accessed before installation" for auth).
 */
let boundController: IdentityController | null = null;
let unbindPrevious: (() => void) | null = null;

/**
 * Bind the shell's projections to the installed SDK's credential holder.
 * Called ONCE by `installSDKAction` after `createSDK`. Synchronous on purpose:
 * the controller's `phase` is already restored (persisted-phase read +
 * restore-time `ClockExpiry` happen inside the SDK's `HolderStore`
 * constructor), so the shell's first render reads the real phase — the
 * synchronous-hydration property the old `getOnInit` storage adapter provided,
 * one layer down where the writer lives.
 *
 * The subscription is the projection pipe: the shell NEVER computes a phase —
 * it renders what the controller holds (app-shell ADR-001 §1, now structural:
 * after the cutover the shell has nothing to compute WITH).
 */
export function bindIdentity(
  controller: IdentityController,
  targetStore: { set: (a: unknown, v: unknown) => void },
): void {
  // A rebind (the boot screen's Retry after a failed install) must REPLACE the
  // pipe, not add a second one — two live subscriptions is two writers for one
  // phase atom, which is the divergence this atom exists to prevent.
  unbindPrevious?.();
  boundController = controller;
  targetStore.set(holderPhaseBaseAtom, controller.phase);
  unbindPrevious = controller.subscribe((phase) => {
    targetStore.set(holderPhaseBaseAtom, phase);
  });
}

export function boundIdentity(): IdentityController {
  if (boundController === null) {
    throw new Error(
      'auth: the SDK credential holder is not bound yet — wait for the bootstrap to complete',
    );
  }
  return boundController;
}

/**
 * THE holder state. Every consumer is a READ-ONLY projection; the writer is
 * the SDK credential holder this atom mirrors.
 */
const holderPhaseBaseAtom = configureAtomDevtools(
  atom<HolderPhase>(INITIAL_PHASE),
  'auth/holderPhaseBase',
);

/**
 * THE holder state. Read-only to every consumer; the ONLY writer is
 * {@link applyAuthEventAction}, which routes through the SDK reducer.
 */
export const authPhaseAtom = configureAtomDevtools(
  atom((get) => get(holderPhaseBaseAtom)),
  'auth/state',
);

/** The phase discriminant alone — what the gates project. */
export const authPhaseNameAtom = configureAtomDevtools(
  atom<HolderPhaseName>((get) => get(authPhaseAtom).phase),
  'auth/phaseName',
);

/** True on every phase that resolves to a tenancy — the bridge's gate. */
export const holdsIdentityAtom = configureAtomDevtools(
  atom((get) => holdsIdentity(get(authPhaseAtom))),
  'auth/holdsIdentity',
);

/** Why a degraded holder is degraded, or null. The indicator text reads this. */
export const authDegradedReasonAtom = configureAtomDevtools(
  atom<DegradedReason | null>((get) => degradedReasonOf(get(authPhaseAtom))),
  'auth/degradedReason',
);

/** The resolved tenancy, or null on a phase that has none. */
export const authIdentityAtom = configureAtomDevtools(
  atom<CredentialIdentity | null>((get) => identityOf(get(authPhaseAtom))),
  'auth/identity',
);

/** The credential material, or null on a phase that holds none. */
export const authMaterialAtom = configureAtomDevtools(
  atom<CredentialMaterial | null>((get) => materialOf(get(authPhaseAtom))),
  'auth/material',
);

/** `exp` in epoch SECONDS (the wire unit), or null when no expiry is known. */
export const authExpiresAtAtom = configureAtomDevtools(
  atom<number | null>((get) => expiresAtOf(get(authPhaseAtom))),
  'auth/expiresAt',
);

// ─────────────────────────────────────────────────────────────────────────────
// Legacy projections — the names the rest of the shell already imports
// ─────────────────────────────────────────────────────────────────────────────

export type AuthClaims = {
  orgId: string | null;
  accountId: string | null;
  roles: string[];
  isTestOrg: boolean;
  isTestAccount: boolean;
  isTestUser: boolean;
  isPlatformAdmin: boolean;
  expiresAt?: number;
};

/**
 * The bearer JWT, projected off the phase. READ-ONLY — there is no longer any
 * way to write a token without going through an event, which is what stopped
 * "set a token, forget the claims" from being representable.
 */
export const tokenAtom = configureAtomDevtools(
  atom<string | null>((get) => get(authMaterialAtom)?.session_jwt ?? null),
  'auth/token',
);

/** The opaque Stytch session token (the renewal key). READ-ONLY projection. */
export const stytchSessionTokenAtom = configureAtomDevtools(
  atom<string | null>((get) => get(authMaterialAtom)?.session_token ?? null),
  'auth/stytchSessionToken',
);

/**
 * The Stytch session JWT. Identical to {@link tokenAtom} by construction —
 * `CredentialMaterial` has exactly one JWT, so "the API token" and "the Stytch
 * JWT" can no longer drift apart the way two independently-written atoms did.
 */
export const stytchSessionJwtAtom = configureAtomDevtools(
  atom<string | null>((get) => get(authMaterialAtom)?.session_jwt ?? null),
  'auth/stytchSessionJwt',
);

/** Non-tenancy display claims (roles / test flags) read off the live JWT. */
function decorativeClaims(sessionJwt: string): Pick<
  AuthClaims,
  'roles' | 'isTestOrg' | 'isTestAccount' | 'isTestUser' | 'isPlatformAdmin'
> {
  const claims = decodeSessionJwt(sessionJwt);
  const raw = claims.raw;
  const rolesValue = raw.roles;
  return {
    roles: Array.isArray(rolesValue)
      ? rolesValue.filter((role): role is string => typeof role === 'string')
      : [],
    isTestOrg: Boolean(raw.is_test_org ?? raw.isTestOrg),
    isTestAccount: Boolean(raw.is_test_account ?? raw.isTestAccount),
    isTestUser: Boolean(raw.is_test_user ?? raw.isTestUser),
    isPlatformAdmin: claims.is_platform_admin,
  };
}

/**
 * Tenancy + display claims, projected off the phase.
 *
 * Null exactly when the holder has no identity. It can no longer report an
 * `orgId` for a holder the machine considers signed out, which is the shape of
 * disagreement that let the bridge context keep syncing a dead tenant.
 */
export const claimsAtom = configureAtomDevtools(
  atom<AuthClaims | null>((get) => {
    const identity = get(authIdentityAtom);
    const material = get(authMaterialAtom);
    if (identity === null || material === null) return null;
    const expiresAt = get(authExpiresAtAtom);
    return {
      orgId: identity.org_id,
      accountId: identity.account_id,
      ...decorativeClaims(material.session_jwt),
      ...(expiresAt === null ? {} : { expiresAt }),
    };
  }),
  'auth/claims',
);

// ─────────────────────────────────────────────────────────────────────────────
// Building events from real material
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Read the tenancy off a session JWT.
 *
 * Returns `null` when the session carries NO `account_id` — a claim-less Stytch
 * session, whose recovery is `activate`, i.e. the `unactivated` phase. That is a
 * legitimate, named state, not a failure.
 *
 * THROWS when the session claims an account but is missing any other field of
 * {@link CredentialIdentity}. A half-tenanted session is a broken credential;
 * substituting a placeholder for the missing claim would manufacture an identity
 * the backend never issued (`.claude/rules/no-fallbacks.md`).
 *
 * Decoding is never a trust decision — the backend re-checks every claim on
 * every request. This reads the caller's OWN credential to learn what the holder
 * just acquired.
 */
export function identityFromSessionJwt(sessionJwt: string): CredentialIdentity | null {
  const claims = decodeSessionJwt(sessionJwt);
  if (claims.account_id === null) {
    return null;
  }
  const missing: string[] = [];
  if (claims.org_id === null) missing.push('org_id');
  if (claims.user_id === null) missing.push('user_id');
  if (claims.email === null) missing.push('email');
  if (missing.length > 0) {
    throw new Error(
      `session JWT claims account_id=${claims.account_id} but is missing ${missing.join(', ')} — ` +
        'an activated LocalAgent session always carries the full tenancy (go/internal/auth/activate.go)',
    );
  }
  return {
    org_id: claims.org_id as string,
    account_id: claims.account_id,
    user_id: claims.user_id as string,
    email: claims.email as string,
  };
}

/**
 * Read `exp` (epoch SECONDS) off a session JWT.
 *
 * THROWS when absent. `HolderPhase`'s `expires_at` is a non-optional number
 * precisely so "I hold a credential and have no idea when it dies" is
 * unrepresentable — the old shell treated a missing `exp` as "no expiry info,
 * therefore not expired", which is how it rendered a signed-in UI over a token
 * that had died 40 minutes earlier.
 */
export function expiresAtFromSessionJwt(sessionJwt: string): number {
  const { exp } = decodeSessionJwt(sessionJwt);
  if (exp === null) {
    throw new Error('session JWT carries no numeric exp claim');
  }
  return exp;
}

/**
 * Sentinel `session_token` meaning "the opaque Stytch renewal key is held by
 * ANOTHER process — this holder is a mirror and cannot renew".
 *
 * `CredentialMaterial.session_token` is non-optional in the SDK contract because
 * on the WEB the opaque token is the only thing that can renew an expired
 * `session_jwt`, so requiring it is what makes `stale` structurally renewable.
 * The DESKTOP renderer is not that holder: the daemon owns the opaque token and
 * does the renewing, and `/auth/info` deliberately returns only the JWT and
 * claims. Writing the JWT into the field, or an empty string, would both claim a
 * renewal key this process does not have — one loudly wrong, one silently.
 *
 * So the mirror says so, in a value that is greppable, assertable
 * ({@link isDelegatedSessionToken}), and impossible to mistake for a credential.
 * Every consumer of `session_token` MUST check it before use.
 */
export {
  DELEGATED_SESSION_TOKEN,
  isDelegatedSessionToken,
  delegatedMaterial,
} from '@localagent/sdk';

/**
 * Turn freshly-acquired material into the local material event it is.
 *
 * One helper, used by every acquisition path (web activate, web rehydrate,
 * org-switch, daemon reconcile, E2E injection), so all of them classify
 * "activated vs claim-less" identically.
 */
export function credentialAcquiredEvent(material: CredentialMaterial): HolderEvent {
  const identity = identityFromSessionJwt(material.session_jwt);
  if (identity === null) {
    return { type: 'CredentialAcquiredUnactivated', material };
  }
  return {
    type: 'CredentialAcquired',
    material,
    identity,
    expires_at: expiresAtFromSessionJwt(material.session_jwt),
  };
}

/** Classify a thrown error into the one adjudication event the machine takes. */
export function verdictEvent(err: unknown, at: number = Date.now()): HolderEvent {
  return { type: 'VerdictObserved', verdict: classifyVerdict(err), at };
}

// ─────────────────────────────────────────────────────────────────────────────
// The single writer
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply a {@link HolderEvent} by routing through the bound SDK credential
 * holder. Kept as the shell's ONE event doorway so the gates read uniformly;
 * the routing is:
 *
 *   - `CredentialAcquired` / `CredentialAcquiredUnactivated` →
 *     `sdk.identity.acquired(material)` (the SDK classifies claim-less vs
 *     activated off the material's own claims);
 *   - `LocalClear` → `sdk.identity.signOut(reason)` — the machine-wide
 *     sign-out, which releases the daemon-held credential on the desktop arm
 *     and runs the registered teardown barriers awaited;
 *   - everything else (verdicts, `ClockExpiry`, `SignInStarted`) →
 *     `sdk.identity.applyEvent(event)`.
 *
 * A refusal is returned, never thrown — the SDK's `TransitionResult` keeps the
 * three outcomes distinguishable.
 */
export const applyAuthEventAction = configureAtomDevtools(
  atom<null, [HolderEvent], Promise<TransitionResult>>(
    null,
    async (_get, _set, event) => {
      const controller = boundIdentity();
      if (event.type === 'CredentialAcquired' || event.type === 'CredentialAcquiredUnactivated') {
        return controller.acquired(event.material);
      }
      if (event.type === 'LocalClear') {
        await controller.signOut(event.reason);
        return { phase: controller.phase, changed: true, refusal: null };
      }
      return controller.applyEvent(event);
    },
  ),
  'auth/applyEvent',
);
